import { HTMLAttributes, memo } from 'react'
import { isValidElement, ReactNode } from 'react'
import ReactMarkdown, { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { JsonCode } from '@/components/ai-elements/json-code'
import { cn } from '@/lib/utils'

/** 移植自 AI Elements message.tsx(仅保留 Message/MessageContent/MessageResponse;
 *  MessageResponse 用 react-markdown + remark-gfm 替代 Streamdown,体积小且无 React 19 要求)。
 */

/** doc 122:```json 围栏块 → JsonCode 语法着色(替代 prose 灰底纯文本,丑);其他语言保持原样 */
const mdComponents: Components = {
  pre: ({ children }) => {
    const child: ReactNode = Array.isArray(children) ? children[0] : children
    if (isValidElement(child)) {
      const props = child.props as { className?: string; children?: ReactNode }
      const lang = /language-(\w+)/.exec(props?.className || '')?.[1]
      if (lang === 'json') {
        const raw = String(props?.children ?? '')
        return <JsonCode code={prettyJson(raw)} maxHeight={360} className="my-2" />
      }
    }
    return <pre>{children}</pre>
  },
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}
export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: 'user' | 'assistant'
}

export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    className={cn(
      'group flex w-full flex-col gap-1',
      from === 'user' ? 'is-user items-end' : 'is-assistant items-start',
      className,
    )}
    {...props}
  />
)

export type MessageContentProps = HTMLAttributes<HTMLDivElement>

export const MessageContent = ({ children, className, ...props }: MessageContentProps) => (
  <div
    className={cn(
      'flex w-fit min-w-0 max-w-[85%] flex-col gap-2 overflow-hidden text-sm',
      'group-[.is-user]:rounded-lg group-[.is-user]:bg-primary group-[.is-user]:px-4 group-[.is-user]:py-2.5 group-[.is-user]:text-primary-foreground',
      'group-[.is-assistant]:text-foreground',
      className,
    )}
    {...props}
  >
    {children}
  </div>
)

export type MessageResponseProps = {
  children: string
  className?: string
}

export const MessageResponse = memo(
  ({ className, children }: MessageResponseProps) => (
    <div
      className={cn(
        'prose prose-sm max-w-none text-foreground',
        '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        // antd reset 与 prose 的协调:链接色对齐 primary
        '[&_a]:text-primary [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em]',
        // doc 105:pre 加 overflow/换行约束 — markdown 代码块长行不再撑爆 grid 中列
        // doc 122 修正:bg 改 muted(浅)后必须同步文字色 — prose 默认 pre-code 是浅色,
        // 浅字+浅底会洗白看不清
        '[&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:text-foreground [&_pre]:p-3 [&_pre]:overflow-x-auto [&_pre]:whitespace-pre-wrap [&_pre]:break-all [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-foreground',
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{children}</ReactMarkdown>
    </div>
  ),
  (prev, next) => prev.children === next.children,
)
MessageResponse.displayName = 'MessageResponse'
